/*

    Working Days: monday, tuesday, wednesday, thursday, friday, saturday
    Checkout Types: special, regular
    Checkout Ready Time: 3.00 PM
    Checkin Time Ranges: before 10.00 AM, after 10.00 AM


    Day                       CheckIn Range                   Regular                         Special
    --------------------------------------------------------------------------------------------------
    Monday to Thursday        Before 10.00 AM                 Day After Next Day              Same Day
    Monday to Thursday        After 10.00 AM                  Day After Day After Next Day    Next Day
    Friday                    Before 10.00 AM                 Day After Day After Next Day    Same Day
    Friday                    After 10.00 AM                  Day After Day After Next Day    Next Day
    Saturday                  Before 10.00 AM                 Day After Day After Next Day    Same Day
    Saturday                  After 10.00 AM                  Day After Day After Next Day    Next Day
*/

import dayjs from "dayjs";
import { storeNow } from "../util/store-clock";
import {
  activeReadyDateRules,
  serviceReadyTime,
  tableReadyDate,
} from "./readyDateTable";
import {
  IStoreClosure,
  dayKey,
  isStoreClosedOn,
  storeClosureOn,
} from "../util/store-closures";
import { useAppSelector } from "../hooks";

export interface IgetCheckOutDateProps {
  isSpecialDay: boolean;
  checkInCutOffTime: Date;
  pickupTime: Date;
  workingDays: string[];
  generalServiceDays: number;
  specialServiceDays: number;
}

export interface IReadyDateDetails {
  date: dayjs.Dayjs;
  /** Holidays the date had to skip (store closure dates), in date order */
  holidays: IStoreClosure[];
  /** True when the date is one the admin set on a skipped holiday */
  isAdminDate: boolean;
}

/**
 * The Special / Regular ready date, with the holidays it had to skip. If the
 * admin set a ready date on a skipped holiday it takes priority (the latest
 * one when several do).
 */
export const getReadyDateDetails = ({
  isSpecialDay,
  checkInCutOffTime,
  pickupTime,
  workingDays,
  generalServiceDays,
  specialServiceDays,
}: IgetCheckOutDateProps) => {
  const checkOutType = isSpecialDay ? "special" : "regular";
  // A service with its own ready time (Workflow page) uses it
  const ownReadyTime = serviceReadyTime(isSpecialDay);
  if (ownReadyTime) {
    const [h, m] = ownReadyTime.split(":").map(Number);
    pickupTime = new Date(pickupTime);
    pickupTime.setHours(h, m, 0, 0);
  }
  // Store wall-clock time, read on every call so dates never go stale
  const now = storeNow();
  const skipped = new Map<string, IStoreClosure>();
  const noteHoliday = (date: Date) => {
    const closure = storeClosureOn(date);
    if (closure) skipped.set(closure.date, closure);
  };
  const withHolidays = (ready: Date): IReadyDateDetails => {
    const holidays = Array.from(skipped.values()).sort((a, b) =>
      a.date.localeCompare(b.date)
    );
    // Admin dates in the past are ignored
    const today = dayKey(now);
    const adminDates = holidays
      .map((h) => (isSpecialDay ? h.specialReadyDate : h.regularReadyDate))
      .filter((d): d is string => !!d && d >= today)
      .sort();
    const adminDate = adminDates[adminDates.length - 1];
    if (adminDate) {
      const [y, m, d] = adminDate.split("-").map(Number);
      const forced = new Date(y, m - 1, d, pickupTime.getHours(), pickupTime.getMinutes(), 0, 0);
      return { date: dayjs(forced), holidays, isAdminDate: true };
    }
    return { date: dayjs(ready), holidays, isAdminDate: false };
  };
  const checkInDate = new Date(now);
  const checkInTimeRange: "morning" | "afternoon" =
    checkInDate.getHours() * 60 + checkInDate.getMinutes() <
    checkInCutOffTime.getHours() * 60 + checkInCutOffTime.getMinutes()
      ? "morning"
      : "afternoon";

  const workingDaysPrepared = [
    workingDays?.includes("SUNDAY") ?? false,
    workingDays?.includes("MONDAY") ?? false,
    workingDays?.includes("TUESDAY") ?? false,
    workingDays?.includes("WEDNESDAY") ?? false,
    workingDays?.includes("THURSDAY") ?? false,
    workingDays?.includes("FRIDAY") ?? false,
    workingDays?.includes("SATURDAY") ?? false,
  ];
  const isWorkingDay = (date: Date) => workingDaysPrepared[date.getDay()];
  // Non-working days and holidays (store closure dates) are always skipped
  const isOpenDay = (date: Date) => isWorkingDay(date) && !isStoreClosedOn(date);

  // Weekly table mode (set in admin) replaces the days-to-deliver rules below
  const tableRules = activeReadyDateRules();
  if (tableRules) {
    const ready = tableReadyDate(
      now,
      isSpecialDay,
      checkInTimeRange === "morning",
      tableRules,
      isWorkingDay,
      isStoreClosedOn,
      noteHoliday
    );
    if (ready) {
      ready.setHours(pickupTime.getHours(), pickupTime.getMinutes(), 0, 0);
      return withHolidays(ready);
    }
    skipped.clear();
  }

  // Dropped off on a non-working day or a holiday: count from the next open
  // day, as if dropped off there before the cut-off
  const dropOffDate = new Date(now);
  let dropOffRange = checkInTimeRange;
  for (let i = 0; i < 14 && !isOpenDay(dropOffDate); i++) {
    noteHoliday(dropOffDate);
    dropOffDate.setDate(dropOffDate.getDate() + 1);
    dropOffRange = "morning";
  }
  if (!isOpenDay(dropOffDate)) {
    // No open day in two weeks (no working days set): keep today
    dropOffDate.setTime(now.getTime());
    dropOffRange = checkInTimeRange;
    skipped.clear();
  }

  const checkOutDate = new Date(dropOffDate);
  let daysToAdd = 0;

  const calculateDaysToAdd = (props: {
    type: "special" | "regular";
    range: "morning" | "afternoon";
  }) => {
    const days =
      (props.type === "special" ? specialServiceDays : generalServiceDays) +
      (props.range === "morning" ? 0 : 1);
    let toAdd = days;
    const startDate = new Date(dropOffDate);
    let startIndex = 0;

    // The guard stops a store with no open days from looping forever
    while (startIndex < days && toAdd < days + 60) {
      startDate.setDate(startDate.getDate() + 1);
      if (
        !isOpenDay(startDate) ||
        (props.type === "regular" && startDate.getDay() === 6)
      ) {
        noteHoliday(startDate);
        toAdd++;
      } else if (props.type === "special" && startDate.getDay() === 2) {
      } else {
        startIndex++;
      }
    }

    return toAdd;
  };

  daysToAdd = calculateDaysToAdd({
    type: checkOutType,
    range: dropOffRange,
  });

  checkOutDate.setDate(dropOffDate.getDate() + daysToAdd);
  checkOutDate.setHours(pickupTime.getHours(), pickupTime.getMinutes(), 0, 0);
  return withHolidays(checkOutDate);
};

const getCheckOutDate = (props: IgetCheckOutDateProps) =>
  getReadyDateDetails(props).date;

export default getCheckOutDate;
